<script setup>
import { nextTick, ref, watch, computed } from 'vue';
import { useTrack } from 'dashboard/composables';
import { COPILOT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';

import CopilotInput from './CopilotInput.vue';
import CopilotLoader from './CopilotLoader.vue';
import CopilotAgentMessage from './CopilotAgentMessage.vue';
import CopilotAssistantMessage from './CopilotAssistantMessage.vue';
import CopilotThinkingGroup from './CopilotThinkingGroup.vue';
import ToggleCopilotAssistant from './ToggleCopilotAssistant.vue';
import CopilotEmptyState from './CopilotEmptyState.vue';
import CopilotThreadDrawer from './CopilotThreadDrawer.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  threads: {
    type: Array,
    default: () => [],
  },
  selectedThreadId: {
    type: [Number, String],
    default: null,
  },
  conversationInboxType: {
    type: String,
    required: true,
  },
  assistants: {
    type: Array,
    default: () => [],
  },
  activeAssistant: {
    type: Object,
    default: () => ({}),
  },
  hasMoreThreads: {
    type: Boolean,
    default: false,
  },
  loadingThreads: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'sendMessage',
  'reset',
  'setAssistant',
  'selectThread',
  'loadMoreThreads',
  'deleteAllThreads',
]);

const { t } = useI18n();
const store = useStore();

// 抽屉状态
const isDrawerOpen = ref(false);

// 下拉菜单和删除对话框
const showActionsDropdown = ref(false);
const deleteDialogRef = ref(null);

const toggleDropdown = () => {
  showActionsDropdown.value = !showActionsDropdown.value;
};

const closeDropdown = () => {
  showActionsDropdown.value = false;
};

const actionMenuItems = computed(() => [
  {
    label: t('PILOT.COPILOT.DELETE_THREAD'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const handleAction = ({ action }) => {
  closeDropdown();
  if (action === 'delete') {
    deleteDialogRef.value?.open();
  }
};

const confirmDelete = async () => {
  if (!props.selectedThreadId) return;

  try {
    await store.dispatch('pilotCopilotThreads/delete', props.selectedThreadId);
    useAlert(t('PILOT.COPILOT.DELETE_THREAD_SUCCESS'));
    emit('reset');
    deleteDialogRef.value?.close();
  } catch (error) {
    useAlert(t('PILOT.COPILOT.DELETE_THREAD_ERROR'));
  }
};

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

const closeDrawer = () => {
  isDrawerOpen.value = false;
};

const handleSelectThread = threadId => {
  emit('selectThread', threadId);
  closeDrawer();
};

const handleNewChat = () => {
  emit('reset');
  closeDrawer();
};

const handleThreadDeleted = threadId => {
  // 如果删除的是当前选中的线程，重置状态
  if (props.selectedThreadId === threadId) {
    emit('reset');
  }
};

const handleDeleteAll = () => {
  emit('reset');
  emit('deleteAllThreads');
};

const sendMessage = message => {
  emit('sendMessage', message);
  useTrack(COPILOT_EVENTS.SEND_MESSAGE);
};

const chatContainer = ref(null);

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

const groupedMessages = computed(() => {
  const result = [];
  let thinkingGroup = [];
  props.messages.forEach(message => {
    if (message.message_type === 'assistant_thinking') {
      thinkingGroup.push(message);
    } else {
      if (thinkingGroup.length > 0) {
        result.push({
          id: thinkingGroup[0].id,
          message_type: 'thinking_group',
          messages: thinkingGroup,
        });
        thinkingGroup = [];
      }
      result.push(message);
    }
  });
  if (thinkingGroup.length > 0) {
    result.push({
      id: thinkingGroup[0].id,
      message_type: 'thinking_group',
      messages: thinkingGroup,
    });
  }
  return result;
});

const isLastMessageFromAssistant = computed(() => {
  if (groupedMessages.value.length === 0) return false;
  return (
    groupedMessages.value[groupedMessages.value.length - 1].message_type ===
    'assistant'
  );
});

const { updateUISettings } = useUISettings();

const closeCopilotPanel = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const handleReset = () => {
  emit('reset');
};

const hasAssistants = computed(() => props.assistants.length > 0);
const hasMessages = computed(() => props.messages.length > 0);

watch(
  [() => props.messages],
  () => {
    scrollToBottom();
  },
  { deep: true }
);
</script>

<template>
  <div
    class="flex flex-col h-full text-sm leading-6 tracking-tight w-full relative"
  >
    <!-- 自定义头部 -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <!-- 左侧：汉堡菜单按钮 -->
      <button
        class="w-8 h-8 flex items-center justify-center bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="toggleDrawer"
      >
        <span class="i-lucide-menu text-n-slate-11 text-lg" />
      </button>
      <!-- 中间：标题 -->
      <span class="font-medium text-n-slate-12">{{
        $t('PILOT.COPILOT.TITLE')
      }}</span>
      <!-- 右侧：更多操作、新聊天、关闭按钮 -->
      <div class="flex items-center gap-1">
        <!-- 更多操作按钮 -->
        <div
          v-if="hasMessages && selectedThreadId"
          v-on-clickaway="closeDropdown"
          class="relative"
        >
          <button
            class="w-8 h-8 flex items-center justify-center hover:bg-n-slate-3 transition-colors"
            @click="toggleDropdown"
          >
            <span class="i-lucide-more-horizontal text-n-slate-11 text-lg" />
          </button>
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="actionMenuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full"
            @action="handleAction"
          />
        </div>
        <button
          v-if="hasMessages"
          class="w-8 h-8 flex items-center justify-center hover:bg-n-slate-3 transition-colors"
          :title="$t('PILOT.COPILOT.NEW_CHAT')"
          @click="handleReset"
        >
          <span class="i-lucide-edit-3 text-n-slate-11 text-lg" />
        </button>
        <button
          class="w-8 h-8 flex items-center justify-center hover:bg-n-slate-3 transition-colors"
          @click="closeCopilotPanel"
        >
          <span class="i-lucide-x text-n-slate-11 text-lg" />
        </button>
      </div>
    </div>

    <!-- 抽屉式线程列表 -->
    <CopilotThreadDrawer
      :is-open="isDrawerOpen"
      :threads="threads"
      :selected-thread-id="selectedThreadId"
      :has-more="hasMoreThreads"
      :loading="loadingThreads"
      @close="closeDrawer"
      @select-thread="handleSelectThread"
      @new-chat="handleNewChat"
      @thread-deleted="handleThreadDeleted"
      @delete-all="handleDeleteAll"
      @load-more="() => emit('loadMoreThreads')"
    />

    <!-- 消息区域 -->
    <div
      ref="chatContainer"
      class="flex-1 flex px-4 py-4 overflow-y-auto items-start"
    >
      <div v-if="hasMessages" class="space-y-6 flex-1 flex flex-col w-full">
        <template v-for="(item, index) in groupedMessages" :key="item.id">
          <CopilotAgentMessage
            v-if="item.message_type === 'user'"
            :message="item.message"
          />
          <CopilotAssistantMessage
            v-else-if="item.message_type === 'assistant'"
            :message="item.message"
            :is-last-message="index === groupedMessages.length - 1"
            :conversation-inbox-type="conversationInboxType"
          />
          <CopilotThinkingGroup
            v-else
            :messages="item.messages"
            :default-collapsed="isLastMessageFromAssistant"
          />
        </template>

        <CopilotLoader v-if="!isLastMessageFromAssistant" />
      </div>
      <CopilotEmptyState
        v-else
        :has-assistants="hasAssistants"
        @use-suggestion="sendMessage"
      />
    </div>

    <!-- 底部输入区域 -->
    <div class="mx-3 mt-px mb-2">
      <div class="flex items-center gap-2 justify-between w-full mb-1">
        <ToggleCopilotAssistant
          v-if="hasAssistants"
          :assistants="assistants"
          :active-assistant="activeAssistant"
          @set-assistant="$event => emit('setAssistant', $event)"
        />
        <div v-else />
      </div>
      <CopilotInput
        v-if="hasAssistants"
        class="mb-1 w-full"
        @send="sendMessage"
      />
    </div>

    <!-- 删除确认对话框 -->
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('PILOT.COPILOT.DELETE_THREAD_TITLE')"
      :description="t('PILOT.COPILOT.DELETE_THREAD_CONFIRM')"
      :confirm-button-label="t('PILOT.COPILOT.DELETE_THREAD')"
      @confirm="confirmDelete"
    />
  </div>
</template>
